/**
 * AgentTalkSystem — walk up to an agent and talk to it.
 *
 * Mirrors the LibrarySystem proximity pattern: a DOM overlay shows a world-
 * anchored "[E] Talk" prompt over the nearest agent; pressing E opens a fixed
 * dialog with what that agent is working on and — the point of the feature —
 * lets you answer its pending Interactions (approve/deny a tool, pick an option,
 * or type a reply), wired to ApiClient.respondToInteraction.
 *
 * Agent world positions are handed in each frame by the caller (the same map
 * StationSystem uses for labels/beams), so this stays decoupled from placement.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import {
  isDefinitiveMutationFailure,
  normalizeChatSendText,
} from '../../api/applicationClient'
import { computeLabelScreenPos } from './worldProjection'
import type { RosterAgent } from './agentRoster'
import type {
  Interaction,
  InteractionAnswer,
  NormalizedRun,
  ProjectedAgentDetail,
  ProjectedChat,
  ProjectedChatMessage,
  ProjectedChatTurn,
  ApplicationChatControls,
  TranscriptEvent,
} from '../../api/types'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import {
  createWorldOverlay,
  escapeWorldHtml,
  type OverlayHandle,
} from './interiorShared'
import {
  getColliders,
  hasClearSightline,
  type AABB,
} from '../colliders'

/** Metres from the camera within which an agent becomes talkable. */
export const TALK_DIST = 3.0
export const TALK_FLOOR_TOLERANCE = 2.5
const PANEL_TITLE_ID = 'arena-agent-talk-title'
const PANEL_DESCRIPTION_ID = 'arena-agent-talk-description'
const ACTIVITY_TAIL = 6
const TOOL_ARGUMENT_LIMIT = 2_048

const STATUS_LABEL: Record<string, string> = {
  running: 'WORKING',
  queued: 'QUEUED',
  completed: 'DONE',
  failed: 'FAILED',
  cancelled: 'CANCELLED',
}
const STATUS_COLOR: Record<string, string> = {
  running: '#7c6cff',
  queued: '#8a93a8',
  completed: '#22d3d3',
  failed: '#d64545',
  cancelled: '#556',
}

interface AgentDetailState {
  agentId: string
  status: 'loading' | 'ready' | 'error'
  detail?: ProjectedAgentDetail
}

interface ChatPanelState {
  chatId: string
  status: 'loading' | 'ready' | 'error'
  chat?: ProjectedChat
}

export interface TalkTarget {
  id: string
  worldPos: readonly [number, number, number]
}

/**
 * Choose only a nearby, same-floor target with a clear eye-to-head sightline.
 * This keeps KeyE ownership identical to the prompt the player can actually
 * see, rather than allowing an invisible agent behind a wall to consume it.
 */
export function nearestVisibleTalkTarget(
  camera: readonly [number, number, number],
  targets: readonly TalkTarget[],
  colliders: readonly AABB[] = [],
  maxDistance = TALK_DIST,
): TalkTarget | null {
  let best: TalkTarget | null = null
  let bestDistance = maxDistance
  for (const target of targets) {
    const [x, y, z] = target.worldPos
    if (Math.abs(camera[1] - y) > TALK_FLOOR_TOLERANCE) continue
    const distance = Math.hypot(x - camera[0], z - camera[2])
    if (distance >= bestDistance) continue
    if (!hasClearSightline(
      { x: camera[0], y: camera[1], z: camera[2] },
      { x, y: y + 2, z },
      colliders,
    )) continue
    best = target
    bestDistance = distance
  }
  return best
}

export interface AgentTalkOptions {
  /**
   * Exactly one app-level AgentTalkSystem owns non-spatial requests from the
   * workplace phone. Room-local systems still support walk-up interaction but
   * must opt out so an inactive room cannot consume another system's request.
   */
  acceptDirectoryRequests?: boolean
}

function compactTime(value: string | undefined): string {
  if (!value) return ''
  const parsed = Date.parse(value)
  if (!Number.isFinite(parsed)) return ''
  return new Date(parsed).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function compactText(value: string, limit: number): string {
  const normalized = value.replace(/\s+/g, ' ').trim()
  return normalized.length > limit ? `${normalized.slice(0, limit - 1)}…` : normalized
}

/** Bounded, escaped representation of Portal-projected approval arguments. */
export function approvalArgumentsHtml(argumentsValue: Record<string, unknown> | undefined): string {
  if (!argumentsValue || Object.keys(argumentsValue).length === 0) return ''
  let serialized: string
  try {
    serialized = JSON.stringify(argumentsValue, null, 2)
  } catch {
    serialized = '[arguments unavailable]'
  }
  if (serialized.length > TOOL_ARGUMENT_LIMIT) {
    serialized = `${serialized.slice(0, TOOL_ARGUMENT_LIMIT - 1)}…`
  }
  return `<div style="color:#69738a;font-size:10px;letter-spacing:.06em;margin-top:5px">ARGUMENTS</div>` +
    `<pre data-approval-arguments="1" style="white-space:pre-wrap;overflow-wrap:anywhere;max-height:150px;overflow:auto;background:#0d1220;border:1px solid #28324a;border-radius:7px;padding:7px;margin:3px 0 8px;font:10px/1.4 ui-monospace,monospace;color:#c8d0e4">${escapeWorldHtml(serialized)}</pre>`
}

/** Portal-owned phase order rendered as a compact done/active/up-next timeline. */
export function phaseTimelineHtml(run: NormalizedRun): string {
  if (run.phases.length === 0) return ''
  const phases = run.phases.map((phase) => {
    const active = phase.status === 'running' || phase.title === run.currentPhase
    const done = phase.status === 'completed'
    const marker = done ? '✓' : active ? '●' : '○'
    const color = done ? '#67e8f9' : active ? '#a78bfa' : '#69738a'
    const state = done ? 'done' : active ? 'active' : 'up next'
    return `<div data-phase-state="${state.replace(' ', '-')}" style="display:grid;grid-template-columns:16px 1fr auto;gap:5px;color:${color};margin:3px 0">` +
      `<span aria-hidden="true">${marker}</span><span>${escapeWorldHtml(phase.title)}</span>` +
      `<span style="font-size:9px;letter-spacing:.05em;text-transform:uppercase">${state}</span></div>`
  }).join('')
  return `<div style="height:1px;background:rgba(255,255,255,0.08);margin:10px 0 8px"></div>` +
    `<div style="font-size:10px;letter-spacing:.08em;color:#8a93a8;margin-bottom:5px">PHASE TIMELINE</div>` +
    `<div data-phase-timeline="1">${phases}</div>`
}

/** Last authoritative transcript events; no parallel transcript state is created. */
export function transcriptActivityHtml(events: readonly TranscriptEvent[]): string {
  const tail = events.slice(-ACTIVITY_TAIL)
  if (tail.length === 0) return ''
  const rows = tail.map((event) => {
    const time = compactTime(event.timestamp)
    return `<div data-activity-event="${escapeWorldHtml(event.id)}" style="display:grid;grid-template-columns:68px 1fr;gap:7px;margin:4px 0">` +
      `<span style="color:#69738a;font-size:9px;text-transform:uppercase">${escapeWorldHtml(event.type.replace(/_/g, ' '))}${time ? `<br>${escapeWorldHtml(time)}` : ''}</span>` +
      `<span style="color:#c8d0e4;overflow-wrap:anywhere">${escapeWorldHtml(compactText(event.content, 180) || 'Activity recorded')}</span></div>`
  }).join('')
  return `<div style="height:1px;background:rgba(255,255,255,0.08);margin:10px 0 8px"></div>` +
    `<div style="font-size:10px;letter-spacing:.08em;color:#8a93a8;margin-bottom:5px">RECENT ACTIVITY · ${tail.length}</div>` +
    `<div data-run-activity="1">${rows}</div>`
}

export function canonicalMessageTimelineHtml(chat: ProjectedChat): string {
  const messages = chat.messages ?? []
  const canonicalCount = Math.max(chat.messageCount ?? messages.length, messages.length)
  const countLabel = canonicalCount === messages.length
    ? `${messages.length} message${messages.length === 1 ? '' : 's'}`
    : `${messages.length} of ${canonicalCount} messages hydrated`
  let html = `<div style="display:flex;justify-content:space-between;gap:8px;margin:3px 0 5px;font-size:10px;color:#69738a">` +
    `<span>CANONICAL TIMELINE</span><span>${countLabel}</span></div>` +
    `<div data-chat-timeline="1" style="display:grid;gap:6px;max-height:250px;overflow-y:auto;margin-bottom:9px">`
  html += messages.length > 0
    ? messages.map((message) => canonicalMessageHtml(message)).join('')
    : `<div style="color:#69738a">No messages yet.</div>`
  return `${html}</div>`
}

function canonicalMessageHtml(message: ProjectedChatMessage): string {
  const time = compactTime(message.createdAt ?? message.timestamp)
  const sequence = message.seq === undefined ? '' : `#${message.seq}`
  const metadata = [message.role ?? 'message', sequence, time].filter(Boolean).join(' · ')
  return `<div data-chat-message-role="${message.role}" ${message.id ? `data-chat-message-id="${escapeWorldHtml(message.id)}"` : ''} style="background:#0d1220;border:1px solid #28324a;border-radius:7px;padding:7px 8px">` +
    `<div style="font-size:10px;color:#69738a;text-transform:uppercase">${escapeWorldHtml(metadata)}</div>` +
    `<div style="white-space:pre-wrap;overflow-wrap:anywhere">${escapeWorldHtml(message.content)}</div></div>`
}

export class AgentTalkSystem {
  private cameraEntity!: pc.Entity
  private container!: HTMLDivElement
  private overlay: OverlayHandle | null = null
  private prompt!: HTMLDivElement
  private panel!: HTMLDivElement
  private readonly tmp = new pc.Vec3()
  private worldCanvas: HTMLCanvasElement | null = null

  private nearestId: string | null = null
  private openId: string | null = null
  private roster: readonly RosterAgent[] = []
  private requestedAgent: RosterAgent | null = null
  private requestedAgentUnavailable = false
  private acceptDirectoryRequests = false
  private disposeInput?: () => void
  private responseError: string | null = null
  private responseNotice: string | null = null
  private detailState: AgentDetailState | null = null
  private detailRequest = 0
  private selectedChatId: string | null = null
  private chatState: ChatPanelState | null = null
  private chatRequest = 0
  private chatSending = false
  private chatStarting = false
  private chatError: string | null = null
  private chatNotice: string | null = null
  private chatTurn: ProjectedChatTurn | null = null
  private readonly chatDrafts = new Map<string, string>()
  private readonly chatControlDrafts = new Map<string, ApplicationChatControls>()
  private chatDraftGeneration = -1
  private unsubscribeStore?: () => void

  /** Register the persistent agent roster (world-space desks). */
  setRoster(roster: readonly RosterAgent[]): void {
    if (
      this.roster === roster ||
      (
        this.roster.length === roster.length &&
        this.roster.every((agent, index) => agent === roster[index])
      )
    ) return
    this.roster = roster
    if (
      this.requestedAgent &&
      roster.some((agent) => agent.agentId === this.requestedAgent?.agentId)
    ) {
      this.requestedAgent = null
      this.requestedAgentUnavailable = false
    }
    if (this.openId?.startsWith('agent:')) this.renderPanel()
  }

  private rosterAgent(id: string): RosterAgent | undefined {
    return this.roster.find((agent) => agent.id === id) ??
      (this.requestedAgent?.id === id ? this.requestedAgent : undefined)
  }

  private directoryAgent(agentId: string): RosterAgent | undefined {
    const spatialAgent = this.roster.find((agent) => agent.agentId === agentId)
    if (spatialAgent) return spatialAgent
    const projectedAgent = useArenaStore.getState().agents.find(
      (agent) => agent.id === agentId,
    )
    if (!projectedAgent) return undefined
    return {
      id: `agent:${projectedAgent.id}`,
      agentId: projectedAgent.id,
      name: projectedAgent.name,
      room: 'WORKPLACE PHONE · Project agent',
      // Directory-opened agents are deliberately non-spatial. This position
      // is never offered to the proximity loop; it only satisfies the shared
      // panel context type.
      worldPos: [0, 0, 0],
    }
  }

  private unavailableDirectoryAgent(agentId: string): RosterAgent {
    return {
      id: `agent:${agentId}`,
      agentId,
      name: 'Agent unavailable',
      room: 'WORKPLACE PHONE · Access changed',
      worldPos: [0, 0, 0],
    }
  }

  private readonly onKeyDown = (e: KeyboardEvent): boolean => {
    if (e.code === 'Escape') {
      if (!this.openId) return false
      this.close()
      return true
    }
    if (this.openId && e.key === 'Tab') {
      const focusable = this.focusablePanelElements()
      e.preventDefault()
      if (focusable.length === 0) {
        this.panel.focus()
        return true
      }
      const current = focusable.indexOf(document.activeElement as HTMLElement)
      const next = e.shiftKey
        ? (current <= 0 ? focusable.length - 1 : current - 1)
        : (current < 0 || current === focusable.length - 1 ? 0 : current + 1)
      focusable[next]?.focus()
      return true
    }
    const target = e.target
    if (
      this.openId &&
      target instanceof HTMLElement &&
      (target.matches('input, textarea, button, select') || target.isContentEditable)
    ) return true
    if (e.code !== 'KeyE') return this.openId !== null && e.key === 'Tab'
    if (this.openId) {
      this.close()
    } else if (this.nearestId) {
      this.open(this.nearestId)
    } else return false
    return true
  }

  init(
    app: pc.Application,
    cameraEntity: pc.Entity,
    options: AgentTalkOptions = {},
  ): void {
    this.cameraEntity = cameraEntity
    this.acceptDirectoryRequests = options.acceptDirectoryRequests ?? false
    this.worldCanvas = app.graphicsDevice.canvas instanceof HTMLCanvasElement
      ? app.graphicsDevice.canvas
      : null

    this.overlay = createWorldOverlay(app, {
      zIndex: '6',
      fontFamily: 'ui-monospace, "SF Mono", monospace',
    })
    this.container = this.overlay.element

    this.prompt = document.createElement('div')
    this.prompt.dataset.testid = 'agent-talk-prompt'
    Object.assign(this.prompt.style, {
      position: 'absolute',
      transform: 'translate(-50%, -100%)',
      padding: '4px 10px',
      borderRadius: '999px',
      background: 'rgba(12,16,26,0.82)',
      border: '1px solid rgba(124,108,255,0.6)',
      color: '#e7ecff',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      display: 'none',
    })
    this.container.appendChild(this.prompt)

    this.panel = document.createElement('div')
    this.panel.dataset.testid = 'agent-talk-dialog'
    this.panel.setAttribute('role', 'dialog')
    this.panel.setAttribute('aria-modal', 'true')
    this.panel.setAttribute('aria-labelledby', PANEL_TITLE_ID)
    this.panel.setAttribute('aria-describedby', PANEL_DESCRIPTION_ID)
    this.panel.tabIndex = -1
    Object.assign(this.panel.style, {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(440px, 86vw)',
      maxHeight: '78vh',
      overflowY: 'auto',
      padding: '18px 20px',
      borderRadius: '14px',
      background: 'rgba(10,14,22,0.94)',
      border: '1px solid rgba(124,108,255,0.55)',
      boxShadow: '0 18px 60px rgba(0,0,0,0.55)',
      color: '#e7ecff',
      fontSize: '13px',
      lineHeight: '1.5',
      pointerEvents: 'auto',
      display: 'none',
    })
    this.container.appendChild(this.panel)

    this.unsubscribeStore = useArenaStore.subscribe((state, previous) => {
      if (
        this.openId &&
        (
          state.apiClient !== previous.apiClient ||
          state.apiGeneration !== previous.apiGeneration
        )
      ) {
        // The panel may contain identity-scoped configuration and conversation
        // data. Dismiss it atomically with an authenticated projection reset,
        // without trying to reacquire world input on the signed-out screen.
        this.close(false, true)
        return
      }
      if (
        this.acceptDirectoryRequests &&
        state.agentTalkRequest !== previous.agentTalkRequest &&
        state.agentTalkRequest
      ) {
        const request = state.agentTalkRequest
        const directoryAgent = this.directoryAgent(request.agentId)
        const rosterAgent = directoryAgent ??
          this.unavailableDirectoryAgent(request.agentId)
        this.requestedAgentUnavailable = directoryAgent === undefined
        this.requestedAgent = this.roster.includes(rosterAgent) ? null : rosterAgent
        this.open(rosterAgent.id)
        useArenaStore.getState().consumeAgentTalkRequest()
        return
      }
      if (!this.openId) return
      if (
        state.runs !== previous.runs ||
        state.interactionsByRun !== previous.interactionsByRun ||
        state.transcriptsByRun !== previous.transcriptsByRun ||
        state.chats !== previous.chats ||
        state.agents !== previous.agents
      ) this.renderPanel()
    })

    this.syncInput()
  }

  /** Per-frame: live-agent (run) world positions from the caller. */
  update(runPositions: ReadonlyMap<string, [number, number, number]>): void {
    if (this.openId) {
      // A live-run dialog closes when its run despawns; roster dialogs persist.
      if (
        this.openId.startsWith('agent:') &&
        !this.rosterAgent(this.openId)
      ) this.close()
      else if (!this.openId.startsWith('agent:') && !this.currentRun(this.openId)) this.close()
      return
    }

    const camComp = this.cameraEntity.camera
    const cam = this.cameraEntity.getPosition()
    const targets: TalkTarget[] = [
      ...[...runPositions].map(([id, worldPos]) => ({ id, worldPos })),
      ...this.roster.map((agent) => ({ id: agent.id, worldPos: agent.worldPos })),
    ]
    const nearest = nearestVisibleTalkTarget(
      [cam.x, cam.y, cam.z],
      targets,
      getColliders(),
    )
    const bestId = nearest?.id ?? null
    const bestPos = nearest?.worldPos ?? null
    this.nearestId = bestId

    if (!bestId || !bestPos || !camComp) {
      this.prompt.style.display = 'none'
      return
    }
    const pos = bestPos as [number, number, number]
    const screen = computeLabelScreenPos(
      pos[0], pos[1] + 2.0, pos[2],
      (wv, out) => camComp.worldToScreen(wv, out),
      this.tmp,
    )
    if (!screen) {
      this.prompt.style.display = 'none'
      return
    }
    this.prompt.textContent = `[E]  Talk to ${this.labelFor(bestId)}`
    this.prompt.style.display = 'block'
    this.prompt.style.left = `${screen.x}px`
    this.prompt.style.top = `${screen.y}px`
  }

  private labelFor(id: string): string {
    const a = this.rosterAgent(id)
    if (a) return a.name
    const run = this.currentRun(id)
    return run ? shortTitle(run) : 'agent'
  }

  private currentRun(id: string): NormalizedRun | undefined {
    return useArenaStore.getState().runs.find((r) => r.id === id)
  }

  private open(id: string): void {
    this.ensureChatDraftOwnership()
    this.openId = id
    // Set modal ownership before releasing pointer lock. ClickToPlay's
    // pointerlockchange handler can then distinguish a chat handoff from an
    // actual user pause, so the pause layer never covers this dialog.
    useArenaStore.getState().setIsAgentTalkOpen(true)
    this.responseError = null
    this.responseNotice = null
    this.nearestId = null
    this.prompt.style.display = 'none'
    document.exitPointerLock?.()
    this.syncInput()
    const rosterAgent = id.startsWith('agent:')
      ? this.rosterAgent(id)
      : undefined
    this.detailState = rosterAgent
      ? { agentId: rosterAgent.agentId, status: 'loading' }
      : null
    const chats = rosterAgent
      ? useArenaStore.getState().chats.filter((chat) => chat.agentId === rosterAgent.agentId)
      : []
    this.selectedChatId = rosterAgent
      ? (chats.find((chat) => chat.canSend) ?? chats[0])?.id ?? null
      : null
    this.chatState = this.selectedChatId
      ? { chatId: this.selectedChatId, status: 'loading' }
      : null
    this.chatError = null
    this.chatNotice = null
    this.chatTurn = null
    this.renderPanel()
    if (rosterAgent) {
      void this.loadAgentDetail(rosterAgent.agentId)
      if (this.selectedChatId) void this.loadChat(this.selectedChatId)
    }
  }

  private close(restoreWorldInput = true, purgePanel = false): void {
    this.openId = null
    this.requestedAgent = null
    this.requestedAgentUnavailable = false
    useArenaStore.getState().setIsAgentTalkOpen(false)
    this.responseError = null
    this.responseNotice = null
    this.detailState = null
    this.detailRequest += 1
    this.selectedChatId = null
    this.chatState = null
    this.chatRequest += 1
    this.chatSending = false
    this.chatStarting = false
    this.chatError = null
    this.chatNotice = null
    this.chatTurn = null
    this.panel.style.display = 'none'
    if (purgePanel) this.panel.replaceChildren()
    this.syncInput()
    if (restoreWorldInput) {
      this.worldCanvas?.focus()
      this.worldCanvas?.requestPointerLock?.()
    }
  }

  private renderPanel(): void {
    this.ensureChatDraftOwnership()
    const id = this.openId
    if (!id) return
    if (id.startsWith('agent:')) {
      this.renderRosterPanel(id)
      return
    }
    const run = this.currentRun(id)
    if (!run) {
      this.close()
      return
    }
    const state = useArenaStore.getState()
    const pending = (state.interactionsByRun[id] ?? []).filter((i) => !i.respondedAt)
    const statusKey = run.status
    const badge = STATUS_LABEL[statusKey] ?? statusKey.toUpperCase()
    const color = STATUS_COLOR[statusKey] ?? '#8a93a8'

    let html =
      `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">` +
      `<span style="font-size:11px;letter-spacing:.08em;color:${color};border:1px solid ${color};` +
      `border-radius:6px;padding:1px 7px">${badge}</span>` +
      `<span style="font-size:11px;color:#8a93a8">AGENT</span></div>` +
      `<div id="${PANEL_TITLE_ID}" style="font-size:15px;font-weight:600;margin-bottom:2px">${escapeWorldHtml(run.title)}</div>` +
      `<div id="${PANEL_DESCRIPTION_ID}" style="position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0">Authorized agent context and actions.</div>`
    if (run.currentPhase) {
      html += `<div style="color:#9aa3ba;font-size:12px;margin-bottom:10px">▸ ${escapeWorldHtml(run.currentPhase)}</div>`
    }
    html += phaseTimelineHtml(run)
    html += transcriptActivityHtml(state.transcriptsByRun[id] ?? [])

    if (pending.length === 0) {
      html +=
        `<div style="color:#9aa3ba;margin-top:8px">` +
        (statusKey === 'running'
          ? 'Heads-down — nothing needs you right now.'
          : 'Idle. No open questions.') +
        `</div>`
    } else {
      html += `<div style="height:1px;background:rgba(255,255,255,0.08);margin:12px 0"></div>`
      html += pending.map((i, idx) => this.interactionHtml(i, idx)).join('')
    }
    html += this.responseErrorHtml()

    html +=
      `<div style="margin-top:16px;text-align:right">` +
      `<button data-act="close" style="${BTN('#334')}">Close  [Esc]</button></div>`

    this.panel.innerHTML = html
    this.panel.style.display = 'block'
    this.wireButtons(pending)
    this.restorePanelFocus(false)
  }

  private renderRosterPanel(id: string): void {
    const composerWasFocused = this.panel.contains(document.activeElement) &&
      document.activeElement instanceof HTMLTextAreaElement &&
      document.activeElement.matches('textarea[data-chat-message="1"]')
    const timeline = this.panel.querySelector<HTMLElement>('[data-chat-timeline="1"]')
    const timelineScrollTop = timeline?.scrollTop ?? 0
    const timelineWasPinned = timeline
      ? timeline.scrollHeight - timeline.clientHeight - timeline.scrollTop < 8
      : true
    const a = this.rosterAgent(id)
    if (!a) {
      this.close()
      return
    }
    const run = a.boundRunId ? this.currentRun(a.boundRunId) : undefined
    const pending = run
      ? (useArenaStore.getState().interactionsByRun[run.id] ?? [])
          .filter((interaction) => !interaction.respondedAt)
      : []
    const status = run?.status ?? 'queued'
    const badge = run ? STATUS_LABEL[status] ?? status.toUpperCase() : 'ON STANDBY'
    const color = run ? STATUS_COLOR[status] ?? '#8a93a8' : '#22d3d3'
    let html =
      `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">` +
      `<span style="font-size:11px;letter-spacing:.08em;color:${color};border:1px solid ${color};` +
      `border-radius:6px;padding:1px 7px">${badge}</span>` +
      `<span style="font-size:11px;color:#8a93a8">${escapeWorldHtml(a.room)}</span></div>` +
      `<div id="${PANEL_TITLE_ID}" style="font-size:15px;font-weight:600;margin-bottom:2px">${escapeWorldHtml(a.name)}</div>` +
      `<div id="${PANEL_DESCRIPTION_ID}" style="position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0">Authorized agent context, bound conversations, and actions.</div>` +
      this.agentDetailHtml(a.agentId)
    if (run) {
      html += `<div style="color:#e7ecff;margin-bottom:2px">${escapeWorldHtml(run.title)}</div>`
      if (run.currentPhase) {
        html += `<div style="color:#9aa3ba;font-size:12px;margin-bottom:10px">▸ ${escapeWorldHtml(run.currentPhase)}</div>`
      }
      html += phaseTimelineHtml(run)
      html += transcriptActivityHtml(useArenaStore.getState().transcriptsByRun[run.id] ?? [])
      html += pending.length > 0
        ? pending.map((interaction, index) => this.interactionHtml(interaction, index)).join('')
        : `<div style="color:#9aa3ba">Heads-down — nothing needs you right now.</div>`
    } else {
      html += this.requestedAgentUnavailable
        ? `<div role="alert" style="color:#fbbf24">This agent is no longer available in the current authorized project. Return to the phone to choose another agent.</div>`
        : `<div style="color:#9aa3ba">Standing by at their project desk.</div>`
    }
    html += this.chatPanelHtml()
    html += this.responseErrorHtml()
    html += `<div style="margin-top:16px;text-align:right">` +
      `<button data-act="close" style="${BTN('#334')}">Close  [Esc]</button></div>`
    this.panel.innerHTML = html
    this.panel.style.display = 'block'
    this.wireButtons(pending)
    queueMicrotask(() => {
      const nextTimeline = this.panel.querySelector<HTMLElement>('[data-chat-timeline="1"]')
      if (!nextTimeline) return
      nextTimeline.scrollTop = timelineWasPinned
        ? nextTimeline.scrollHeight
        : Math.min(timelineScrollTop, nextTimeline.scrollHeight)
    })
    this.restorePanelFocus(composerWasFocused)
  }

  private async loadAgentDetail(agentId: string): Promise<void> {
    const request = ++this.detailRequest
    const state = useArenaStore.getState()
    const client = state.apiClient
    const generation = state.apiGeneration
    if (!client?.getAgentDetail) {
      if (this.openId === `agent:${agentId}`) {
        this.detailState = { agentId, status: 'error' }
        this.renderPanel()
      }
      return
    }
    try {
      const detail = await client.getAgentDetail(agentId)
      const current = useArenaStore.getState()
      if (
        request !== this.detailRequest ||
        this.openId !== `agent:${agentId}` ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      this.detailState = { agentId, status: 'ready', detail }
    } catch {
      const current = useArenaStore.getState()
      if (
        request !== this.detailRequest ||
        this.openId !== `agent:${agentId}` ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      this.detailState = { agentId, status: 'error' }
    }
    this.renderPanel()
  }

  private agentDetailHtml(agentId: string): string {
    const state = this.detailState
    if (!state || state.agentId !== agentId || state.status === 'loading') {
      return `<div style="color:#69738a;font-size:11px;margin-bottom:12px">Checking configuration access…</div>`
    }
    if (state.status === 'error' || !state.detail) {
      return `<div role="status" style="color:#fbbf24;font-size:11px;margin-bottom:12px">Configuration could not be loaded. Roster access is unchanged.</div>`
    }
    return agentConfigurationHtml(state.detail)
  }

  private chatPanelHtml(): string {
    const rosterAgent = this.openId
      ? this.rosterAgent(this.openId)
      : undefined
    const chats = rosterAgent
      ? useArenaStore.getState().chats.filter((chat) => chat.agentId === rosterAgent.agentId)
      : []
    let html =
      `<div style="height:1px;background:rgba(255,255,255,0.08);margin:14px 0 10px"></div>` +
      `<div style="font-size:11px;letter-spacing:.08em;color:#8a93a8;margin-bottom:7px">BOUND CONVERSATIONS</div>`
    if (chats.length === 0) {
      const projectedAgent = rosterAgent
        ? useArenaStore.getState().agents.find((agent) => agent.id === rosterAgent.agentId)
        : undefined
      if (projectedAgent?.canStartChat) {
        return html +
          `<div role="status" style="color:#69738a;margin-bottom:8px">No conversation yet. Start a private, canonically bound chat with this agent.</div>` +
          `<button data-act="start-chat" ${this.chatStarting ? 'disabled' : ''} style="${BTN('#4b4780')}">` +
          `${this.chatStarting ? 'Starting…' : 'Start conversation'}</button>` +
          `${this.chatError ? `<div role="alert" style="margin-top:7px;color:#fca5a5">${escapeWorldHtml(this.chatError)}</div>` : ''}`
      }
      return html + `<div role="status" style="color:#69738a">No authorized conversation is bound to this agent, and starting one is not allowed for the current user.</div>`
    }
    html += `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px">`
    html += chats.slice(0, 8).map((chat) => {
      const selected = chat.id === this.selectedChatId
      return `<button data-act="select-chat" data-chat="${escapeWorldHtml(chat.id)}" ` +
        `${this.chatSending ? 'disabled' : ''} ` +
        `aria-pressed="${selected}" style="${BTN(selected ? '#4b4780' : '#262c3d')}">` +
        `${escapeWorldHtml(chat.title)}</button>`
    }).join('')
    html += `</div>`
    if (chats.length > 8) {
      html += `<label style="display:block;color:#69738a;font-size:11px;margin-bottom:9px">All bound conversations ` +
        `<select data-chat-select="1" ${this.chatSending ? 'disabled' : ''} ` +
        `style="display:block;width:100%;margin-top:3px;background:#0d1220;border:1px solid #33405e;border-radius:7px;color:#e7ecff;padding:6px 8px;font:inherit">` +
        chats.map((chat) => `<option value="${escapeWorldHtml(chat.id)}" ${chat.id === this.selectedChatId ? 'selected' : ''}>${escapeWorldHtml(chat.title)}</option>`).join('') +
        `</select></label>`
    }

    const state = this.chatState
    if (!this.selectedChatId || !state || state.chatId !== this.selectedChatId || state.status === 'loading') {
      return html + `<div role="status" style="color:#69738a">Loading canonical conversation…</div>`
    }
    if (state.status === 'error' || !state.chat) {
      return html + `<div role="alert" style="color:#fca5a5">${escapeWorldHtml(this.chatError ?? 'Conversation could not be loaded.')}</div>`
    }
    html += canonicalMessageTimelineHtml(state.chat)
    if (state.chat.canSend) {
      const draft = this.chatDrafts.get(state.chat.id) ?? ''
      const draftValid = normalizeChatSendText(draft) !== null
      const allowed = state.chat.allowedControls
      const selectedControls = this.chatControlDrafts.get(state.chat.id)
      const controlSelect = (
        field: string,
        label: string,
        options: readonly (string | boolean)[],
        selected: string | boolean | undefined,
      ) => `<label style="color:#9aa3ba;font-size:11px">${label}<select data-chat-control="${field}" ` +
        `${this.chatSending ? 'disabled' : ''} style="display:block;width:100%;margin-top:3px;background:#0d1220;` +
        `border:1px solid #33405e;border-radius:7px;color:#e7ecff;padding:5px 7px;font:inherit">` +
        `<option value="">Inherit profile</option>` +
        options.map((option) => `<option value="${String(option)}" ${selected === option ? 'selected' : ''}>` +
          `${escapeWorldHtml(String(option))}</option>`).join('') + `</select></label>`
      if (allowed) {
        html += `<details data-chat-controls="1" style="margin:0 0 8px;border:1px solid #28324a;border-radius:7px;padding:6px 8px">` +
          `<summary style="cursor:pointer;color:#9aa3ba;font-size:11px">Portal-authorized agent controls</summary>` +
          `<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;margin-top:7px">` +
          controlSelect('approvals', 'Approvals', allowed.approvals, selectedControls?.approvals) +
          controlSelect('reasoning_effort', 'Reasoning', allowed.reasoning_effort, selectedControls?.reasoning_effort) +
          controlSelect('permission_intent', 'Permission', allowed.permission_intent, selectedControls?.permission_intent) +
          controlSelect('profile_ref', 'Profile', allowed.profile_ref, selectedControls?.profile_ref) +
          controlSelect('skill_ref', 'Skill', allowed.skill_ref, selectedControls?.skill_ref) +
          `</div></details>`
      }
      html +=
        `<div style="display:flex;gap:6px;align-items:flex-end">` +
        `<textarea data-chat-message="1" maxlength="8000" rows="2" ` +
        `aria-label="Message for ${escapeWorldHtml(state.chat.title)}" placeholder="Write a message…" ` +
        `${this.chatSending ? 'disabled' : ''} ` +
        `style="flex:1;resize:vertical;background:#0d1220;border:1px solid #33405e;border-radius:7px;color:#e7ecff;padding:7px 9px;font:inherit">${escapeWorldHtml(draft)}</textarea>` +
        `<button data-act="chat-send" ${this.chatSending || !draftValid ? 'disabled' : ''} style="${BTN('#3a3f66')}">` +
        `${this.chatSending ? 'Sending…' : 'Send'}</button></div>`
      html += `<div data-chat-validation="1" role="status" style="margin-top:5px;color:#fbbf24;font-size:11px">` +
        `${draft && !draftValid ? 'Message must be 1–4,000 characters, at most 8 KiB, with no control characters.' : ''}</div>`
    } else {
      html += `<div role="status" style="color:#69738a">This conversation is read-only for the current user.</div>`
    }
    if (this.chatError) {
      html += `<div role="alert" style="margin-top:7px;color:#fca5a5">${escapeWorldHtml(this.chatError)}</div>`
    } else if (this.chatNotice) {
      const status = this.chatTurn?.status ?? 'completed'
      const label = status === 'completed'
        ? 'ACCEPTED · ASSISTANT COMPLETED'
        : status === 'assistant_failed'
          ? 'ACCEPTED · ASSISTANT FAILED'
          : 'ACCEPTED · ASSISTANT INTERRUPTED'
      html += `<div data-chat-turn-status="${status}" role="status" style="margin-top:7px;color:#67e8f9">` +
        `<div style="font-size:10px;letter-spacing:.07em">${label}</div>` +
        `${escapeWorldHtml(this.chatNotice)}</div>`
    }
    return html
  }

  private async loadChat(chatId: string): Promise<void> {
    const request = ++this.chatRequest
    const { apiClient: client, apiGeneration: generation } = useArenaStore.getState()
    const expectedAgentId = this.openId
      ? this.rosterAgent(this.openId)?.agentId
      : undefined
    if (!client?.getChat) {
      this.chatState = { chatId, status: 'error' }
      this.renderPanel()
      return
    }
    try {
      const chat = await client.getChat(chatId)
      const current = useArenaStore.getState()
      if (
        request !== this.chatRequest ||
        this.selectedChatId !== chatId ||
        !this.openId?.startsWith('agent:') ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      if (!expectedAgentId || chat.agentId !== expectedAgentId) {
        this.chatState = { chatId, status: 'error' }
        this.chatError = 'Conversation identity no longer matches this agent.'
        this.renderPanel()
        return
      }
      const draft = this.chatControlDrafts.get(chatId)
      const allowed = chat.allowedControls
      if (
        !allowed ||
        (draft?.approvals !== undefined && !allowed.approvals.includes(draft.approvals)) ||
        (draft?.reasoning_effort !== undefined && !allowed.reasoning_effort.includes(draft.reasoning_effort)) ||
        (draft?.permission_intent !== undefined && !allowed.permission_intent.includes(draft.permission_intent)) ||
        (draft?.profile_ref !== undefined && !allowed.profile_ref.includes(draft.profile_ref)) ||
        (draft?.skill_ref !== undefined && !allowed.skill_ref.includes(draft.skill_ref))
      ) this.chatControlDrafts.delete(chatId)
      this.chatState = { chatId, status: 'ready', chat }
    } catch {
      const current = useArenaStore.getState()
      if (
        request !== this.chatRequest ||
        this.selectedChatId !== chatId ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      this.chatState = { chatId, status: 'error' }
    }
    this.renderPanel()
  }

  private async startAgentChat(): Promise<void> {
    if (this.chatStarting || !this.openId?.startsWith('agent:')) return
    const rosterAgent = this.rosterAgent(this.openId)
    if (!rosterAgent) return
    const { apiClient: client, apiGeneration: generation } = useArenaStore.getState()
    if (!client?.startAgentChat) return
    const request = ++this.chatRequest
    this.chatStarting = true
    this.chatError = null
    this.chatNotice = null
    this.renderPanel()
    try {
      const chat = await client.startAgentChat(rosterAgent.agentId)
      const current = useArenaStore.getState()
      if (
        request !== this.chatRequest ||
        this.openId !== rosterAgent.id ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      if (!chat.canSend || chat.agentId !== rosterAgent.agentId) {
        throw new Error('Invalid agent chat projection')
      }
      current.upsertChat(chat)
      this.selectedChatId = chat.id
      this.chatState = { chatId: chat.id, status: 'ready', chat }
      this.chatNotice = 'Private conversation created. You can message this agent now.'
    } catch (error) {
      const current = useArenaStore.getState()
      if (
        request !== this.chatRequest ||
        this.openId !== rosterAgent.id ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      this.chatError = isDefinitiveMutationFailure(error)
        ? 'Conversation could not be created. Access may have changed.'
        : 'Creation is still being confirmed. Retry to reuse the same operation key.'
    } finally {
      if (request === this.chatRequest) this.chatStarting = false
    }
    this.renderPanel()
  }

  private async sendChat(): Promise<void> {
    const chatId = this.selectedChatId
    const input = this.panel.querySelector<HTMLTextAreaElement>('textarea[data-chat-message="1"]')
    const message = input?.value.trim() ?? ''
    if (!chatId || this.chatSending) return
    const normalizedMessage = normalizeChatSendText(message)
    if (!normalizedMessage) {
      this.chatError = 'Message must be 1–4,000 characters, at most 8 KiB, with no control characters.'
      this.renderPanel()
      return
    }
    const { apiClient: client, apiGeneration: generation } = useArenaStore.getState()
    const expectedAgentId = this.openId
      ? this.rosterAgent(this.openId)?.agentId
      : undefined
    if (!client?.sendChatMessage) return
    const request = ++this.chatRequest
    this.chatDrafts.set(chatId, normalizedMessage)
    this.chatSending = true
    this.chatError = null
    this.chatNotice = null
    this.chatTurn = null
    this.renderPanel()
    try {
      const controls = this.chatControlDrafts.get(chatId)
      const result = controls
        ? await client.sendChatMessage(chatId, normalizedMessage, controls)
        : await client.sendChatMessage(chatId, normalizedMessage)
      const current = useArenaStore.getState()
      if (
        request !== this.chatRequest ||
        this.selectedChatId !== chatId ||
        !this.openId?.startsWith('agent:') ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      if (!expectedAgentId || result.chat.agentId !== expectedAgentId) {
        this.chatState = { chatId, status: 'error' }
        this.chatDrafts.delete(chatId)
        this.chatError = 'Conversation identity changed; the response was not attached to this agent.'
      } else {
        this.chatState = { chatId, status: 'ready', chat: result.chat }
        this.chatDrafts.delete(chatId)
        this.chatTurn = result.turn
        this.chatNotice = result.turn.status === 'completed'
          ? 'Assistant response confirmed by the canonical conversation.'
          : result.turn.status === 'assistant_failed'
            ? 'The canonical user message was accepted once, but the assistant failed.'
            : 'The canonical user message was accepted once, but assistant execution was interrupted.'
      }
    } catch (error) {
      const current = useArenaStore.getState()
      if (
        request !== this.chatRequest ||
        this.selectedChatId !== chatId ||
        current.apiClient !== client ||
        current.apiGeneration !== generation
      ) return
      const definitive = isDefinitiveMutationFailure(error)
      this.chatError = definitive
        ? 'Message was not accepted. Authorization or conversation access changed.'
        : 'Message outcome is uncertain. Retry here to reuse the same operation key; do not refresh.'
    } finally {
      if (request === this.chatRequest) this.chatSending = false
    }
    this.renderPanel()
  }

  private interactionHtml(i: Interaction, idx: number): string {
    let body = `<div style="margin-bottom:6px">${escapeWorldHtml(i.prompt || i.toolName || 'Awaiting your input')}</div>`
    if (i.type === 'approval') {
      if (i.toolName) {
        body += `<div style="color:#9aa3ba;font-size:11px;margin-bottom:8px">tool: ${escapeWorldHtml(i.toolName)}</div>`
      }
      body += approvalArgumentsHtml(i.toolArguments)
      body +=
        `<div style="display:flex;gap:8px">` +
        `<button data-i="${idx}" data-act="approve" style="${BTN('#1f7a52')}">Approve</button>` +
        `<button data-i="${idx}" data-act="deny" style="${BTN('#7a2f2f')}">Deny</button></div>`
    } else {
      const q = i.questions?.[0]
      const opts = q?.options ?? []
      if (opts.length) {
        body +=
          `<div style="display:flex;flex-wrap:wrap;gap:6px">` +
          opts
            .map(
              (o, oi) =>
                `<button data-i="${idx}" data-act="opt" data-opt="${oi}" style="${BTN('#3a3f66')}">${escapeWorldHtml(o.label)}</button>`,
            )
            .join('') +
          `</div>`
      } else {
        body +=
          `<div style="display:flex;gap:6px">` +
          `<input data-i="${idx}" data-inp="1" placeholder="Type a reply…" ` +
          `style="flex:1;background:#0d1220;border:1px solid #33405e;border-radius:7px;color:#e7ecff;padding:6px 9px;font:inherit"/>` +
          `<button data-i="${idx}" data-act="send" style="${BTN('#3a3f66')}">Send</button></div>`
      }
    }
    return `<div style="margin-bottom:14px">${body}</div>`
  }

  private wireButtons(pending: Interaction[]): void {
    this.panel.querySelectorAll<HTMLSelectElement>('select[data-chat-control]').forEach((select) => {
      select.addEventListener('change', () => {
        if (!this.selectedChatId || this.chatSending) return
        const controls: ApplicationChatControls = { schema: 'animus.chat.application_controls.v1' }
        this.panel.querySelectorAll<HTMLSelectElement>('select[data-chat-control]').forEach((candidate) => {
          const field = candidate.dataset.chatControl
          const value = candidate.value
          if (!field || !value) return
          if (field === 'approvals') controls.approvals = value === 'true'
          else if (field === 'reasoning_effort') controls.reasoning_effort = value as ApplicationChatControls['reasoning_effort']
          else if (field === 'permission_intent') controls.permission_intent = value as ApplicationChatControls['permission_intent']
          else if (field === 'profile_ref') controls.profile_ref = value
          else if (field === 'skill_ref') controls.skill_ref = value
        })
        if (Object.keys(controls).length === 1) this.chatControlDrafts.delete(this.selectedChatId)
        else this.chatControlDrafts.set(this.selectedChatId, controls)
      })
    })
    this.panel.querySelector<HTMLTextAreaElement>('textarea[data-chat-message="1"]')
      ?.addEventListener('input', (event) => {
        if (!this.selectedChatId) return
        this.chatDrafts.set(
          this.selectedChatId,
          (event.currentTarget as HTMLTextAreaElement).value,
        )
        const value = (event.currentTarget as HTMLTextAreaElement).value
        const valid = normalizeChatSendText(value) !== null
        const button = this.panel.querySelector<HTMLButtonElement>('button[data-act="chat-send"]')
        if (button) button.disabled = !valid || this.chatSending
        const validation = this.panel.querySelector<HTMLElement>('[data-chat-validation="1"]')
        if (validation) {
          validation.textContent = value && !valid
            ? 'Message must be 1–4,000 characters, at most 8 KiB, with no control characters.'
            : ''
        }
        ;(event.currentTarget as HTMLTextAreaElement).setAttribute('aria-invalid', String(Boolean(value && !valid)))
      })
    this.panel.querySelector<HTMLSelectElement>('select[data-chat-select="1"]')
      ?.addEventListener('change', (event) => {
        if (this.chatSending) return
        const chatId = (event.currentTarget as HTMLSelectElement).value
        if (!chatId || chatId === this.selectedChatId) return
        this.selectedChatId = chatId
        this.chatState = { chatId, status: 'loading' }
        this.chatError = null
        this.chatNotice = null
        this.chatTurn = null
        this.renderPanel()
        void this.loadChat(chatId)
      })
    this.panel.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const act = btn.getAttribute('data-act')
        if (act === 'close') {
          this.close()
          return
        }
        if (act === 'select-chat') {
          if (this.chatSending) return
          const chatId = btn.getAttribute('data-chat')
          if (!chatId || chatId === this.selectedChatId) return
          this.selectedChatId = chatId
          this.chatState = { chatId, status: 'loading' }
          this.chatError = null
          this.chatNotice = null
          this.chatTurn = null
          this.renderPanel()
          void this.loadChat(chatId)
          return
        }
        if (act === 'chat-send') {
          void this.sendChat()
          return
        }
        if (act === 'start-chat') {
          void this.startAgentChat()
          return
        }
        const idx = Number(btn.getAttribute('data-i'))
        const interaction = pending[idx]
        if (!interaction) return
        if (act === 'approve' || act === 'deny') {
          void this.respond(interaction, { kind: 'approval', allow: act === 'approve' })
        } else if (act === 'opt') {
          const oi = Number(btn.getAttribute('data-opt'))
          const q = interaction.questions?.[0]
          const label = q?.options?.[oi]?.label ?? ''
          void this.respond(interaction, {
            kind: 'structured',
            selects: [{ question: q?.question ?? interaction.prompt, labels: [label] }],
          })
        } else if (act === 'send') {
          const inp = this.panel.querySelector<HTMLInputElement>(`input[data-i="${idx}"]`)
          const text = inp?.value.trim()
          if (text) void this.respond(interaction, { kind: 'text', text })
        }
      })
    })
  }

  private async respond(interaction: Interaction, answer: InteractionAnswer): Promise<void> {
    this.responseError = null
    this.responseNotice = null
    const result = await useArenaStore.getState()
      .submitInteractionResponse(interaction, answer)
    if (result.status === 'accepted') {
      if (!result.refreshed) {
        this.responseNotice =
          'Response accepted. Live state refresh is temporarily unavailable.'
      }
    } else if (result.status !== 'already-answered') {
      this.responseError =
        'Response was not accepted. The request may have expired or already been answered; refresh and try again.'
    }
    this.renderPanel()
  }

  private responseErrorHtml(): string {
    if (this.responseError) {
      return `<div role="alert" style="margin-top:10px;color:#fca5a5">${escapeWorldHtml(this.responseError)}</div>`
    }
    return this.responseNotice
      ? `<div role="status" style="margin-top:10px;color:#67e8f9">${escapeWorldHtml(this.responseNotice)}</div>`
      : ''
  }

  destroy(): void {
    if (this.openId) useArenaStore.getState().setIsAgentTalkOpen(false)
    this.unsubscribeStore?.()
    this.unsubscribeStore = undefined
    this.disposeInput?.()
    this.disposeInput = undefined
    this.detailRequest += 1
    this.chatRequest += 1
    this.chatDrafts.clear()
    this.chatControlDrafts.clear()
    this.requestedAgent = null
    this.requestedAgentUnavailable = false
    this.detailState = null
    this.overlay?.dispose()
    this.overlay = null
    this.worldCanvas = null
  }

  private syncInput(): void {
    this.disposeInput?.()
    this.disposeInput = modalInputRouter.register(
      this.openId ? INPUT_PRIORITY.interaction : INPUT_PRIORITY.room,
      this.onKeyDown,
    )
  }

  private ensureChatDraftOwnership(): void {
    const generation = useArenaStore.getState().apiGeneration
    if (generation === this.chatDraftGeneration) return
    this.chatDrafts.clear()
    this.chatControlDrafts.clear()
    this.chatDraftGeneration = generation
  }

  private focusablePanelElements(): HTMLElement[] {
    return Array.from(this.panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => !element.hasAttribute('hidden'))
  }

  private restorePanelFocus(preferComposer: boolean): void {
    queueMicrotask(() => {
      if (!this.openId || this.panel.style.display === 'none') return
      const composer = this.panel.querySelector<HTMLTextAreaElement>(
        'textarea[data-chat-message="1"]:not([disabled])',
      )
      const target: HTMLElement = preferComposer && composer
        ? composer
        : composer ?? this.focusablePanelElements()[0] ?? this.panel
      target.focus()
      if (composer && target === composer) {
        const end = composer.value.length
        composer.setSelectionRange(end, end)
      }
    })
  }
}

export function agentConfigurationHtml(detail: ProjectedAgentDetail): string {
  if (!detail.canInspect || !detail.configuration) {
    const reason = detail.redactionReason === 'configuration_unavailable'
      ? 'No configuration fields are available for this agent.'
      : 'Configuration is hidden because this session is not authorized to inspect this agent.'
    return `<div role="status" style="color:#69738a;font-size:11px;margin-bottom:12px">${reason}</div>`
  }
  const config = detail.configuration
  const row = (label: string, value: string | number | undefined): string =>
    `<div style="display:grid;grid-template-columns:92px 1fr;gap:8px;margin:3px 0">` +
    `<span style="color:#69738a">${label}</span>` +
    `<span>${escapeWorldHtml(value === undefined || value === '' ? 'Not set' : String(value))}</span></div>`
  const list = (value: readonly string[]): string => value.length > 0
    ? value.map(escapeWorldHtml).join(', ')
    : 'None'
  let html =
    `<div style="height:1px;background:rgba(255,255,255,0.08);margin:10px 0"></div>` +
    `<div style="font-size:11px;letter-spacing:.08em;color:#8a93a8;margin-bottom:5px">AUTHORIZED CONFIGURATION</div>`
  if (config.description) {
    html += `<div style="color:#c8d0e4;margin-bottom:8px">${escapeWorldHtml(config.description)}</div>`
  }
  html += row('Model', config.model)
  html += row('Provider', config.provider)
  html += row('Tool', config.tool)
  html += row('Reasoning', config.reasoningEffort)
  html += row('Max tokens', config.maxTokens)
  html += row('Temperature', config.temperature)
  html += row('Skills', list(config.skills))
  html += row('MCP servers', list(config.mcpServers))
  html += row('Memory', config.memory ? (config.memory.enabled ? 'Enabled' : 'Disabled') : undefined)
  html += row(
    'Communication',
    config.communication
      ? `${config.communication.enabled ? 'Enabled' : 'Disabled'} · ${list(config.communication.channels)}`
      : undefined,
  )
  html += row('Tool allow', config.toolPolicy ? list(config.toolPolicy.allow) : undefined)
  html += row('Tool deny', config.toolPolicy ? list(config.toolPolicy.deny) : undefined)
  html += `<div style="color:#69738a;margin-top:8px">System prompt</div>`
  html += `<pre style="white-space:pre-wrap;overflow-wrap:anywhere;background:#0d1220;border:1px solid #28324a;border-radius:7px;padding:8px;margin:4px 0 12px;font:11px/1.45 ui-monospace,monospace;color:#c8d0e4">${escapeWorldHtml(config.systemPrompt ?? 'Not published')}</pre>`
  return html
}

/** A pill button style string. */
function BTN(bg: string): string {
  return (
    `background:${bg};border:1px solid rgba(255,255,255,0.14);border-radius:8px;` +
    `color:#eef2ff;padding:6px 12px;font:inherit;cursor:pointer`
  )
}

function shortTitle(run: NormalizedRun): string {
  const t = run.title ?? 'agent'
  return t.length > 22 ? `${t.slice(0, 21)}…` : t
}
